import { type NamedExoticComponent, type ReactElement, type ReactNode, memo } from "react"

import { useMount, useUnmount } from "react-use"

import { ViewAsBanner } from "@/3514/components"
import { useIsFakeParticipant } from "@/3514/hooks"
import { type IAppState, type TAppDispatcher, useAppDispatcher, useAppSelector } from "@/3514/store/app"
import { buildCssClassName } from "@/3514/utils"
import ParticipantLayoutHeader from "@/elements/participant-layout-header/ParticipantLayoutHeader"
import { FullSpinner } from "@/shared/spinner"

import "../styles.scss"

enum EParticipantLayoutTag {
    Main = "main",
    Section = "section"
}

interface IProps {
    leftSidebar?: ReactElement | null
    header?: ReactElement | ReactNode | null
    closeMenu?: boolean
    className?: string
    isLoading?: boolean
    showLogoOnMobile?: boolean
    noPadding?: boolean
    title?: ReactNode
    children?: ReactNode
    tag?: EParticipantLayoutTag
}

/**
 * @deprecated Legacy stuff. Should be rewritten A$AP.
 *
 * 24\09\2025 UPD: only 2 simulab (coaches list and chat) pages still use this file bc of custom header
 *
 */
const ParticipantLayout: NamedExoticComponent<IProps> = memo(
    ({
        children,
        leftSidebar = null,
        header = null,
        closeMenu = false,
        isLoading = false,
        noPadding = false,
        showLogoOnMobile = true,
        title = null,
        className = "",
        tag: Tag = EParticipantLayoutTag.Section
    }: IProps): ReactElement => {
        const { isOpenBurgerMenu, isOpenSidebar }: IAppState = useAppSelector()

        const isFakeParticipant: boolean = useIsFakeParticipant()

        const appDispatcher: TAppDispatcher = useAppDispatcher()

        useMount((): void => {
            if (closeMenu) appDispatcher.closeSidebar()
        })

        useUnmount((): void => {
            appDispatcher.openSidebar()
        })

        return (
            <>
                {leftSidebar}
                <Tag
                    className={buildCssClassName(
                        "participant-layout",
                        className,
                        isOpenSidebar ? "md:ml-[230px] lg:ml-[261px] " : "ml-[0px] md:ml-[90px]",
                        {
                            "show-spacing": !noPadding && !!header,
                            "gray-bg": isOpenBurgerMenu
                        }
                    )}
                >
                    <ParticipantLayoutHeader header={header} showLogo={showLogoOnMobile} />
                    {isFakeParticipant && <ViewAsBanner />}
                    {title && (
                        <div
                            className={buildCssClassName("participant-layout-page-title", {
                                "fake-mode": isFakeParticipant
                            })}
                        >
                            {title}
                        </div>
                    )}
                    {isLoading ? <FullSpinner /> : children}
                </Tag>
            </>
        )
    }
)

ParticipantLayout.displayName = "ParticipantLayout"

export { EParticipantLayoutTag, ParticipantLayout }
